import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import {
  api as accountsApi,
  type Account,
  type Payout,
  type PayoutInput,
} from "../lib/api";

/**
 * Create + edit form for payouts. Same pattern as TradeLogForm — account
 * dropdown drives the prop_firm snapshot.
 */
export default function PayoutForm({
  title,
  initial,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial?: Payout;
  onSubmit: (input: PayoutInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [date, setDate] = useState<string>(
    initial?.date ? toInputDate(initial.date) : toInputDate(new Date().toISOString()),
  );
  const [accountId, setAccountId] = useState(initial?.account_id ?? "");
  const [propFirm, setPropFirm] = useState(initial?.prop_firm ?? "");
  const [amount, setAmount] = useState<string>(initial ? String(initial.amount) : "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Single fetcher reused on mount AND on later refresh triggers (tab focus,
  // dropdown open). Picks up activate/deactivate flips made elsewhere without
  // forcing the user to close and reopen the form.
  const refresh = useCallback(async () => {
    try {
      const accs = await accountsApi.list();
      if (!mountedRef.current) return;
      setAccounts(accs);
      setLoadError(null);
    } catch (e: unknown) {
      if (!mountedRef.current) return;
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const accountOptions = useMemo(() => {
    if (!accounts) return [];
    const active = accounts.filter((a) => a.active);
    const sorted = [...active].sort((a, b) => a.name.localeCompare(b.name));
    if (initial && !active.some((a) => a.account_id === initial.account_id)) {
      const inactive = accounts.find((a) => a.account_id === initial.account_id);
      if (inactive) sorted.unshift(inactive);
    }
    return sorted;
  }, [accounts, initial]);

  function onAccountChange(value: string) {
    setAccountId(value);
    const account = accounts?.find((a) => a.account_id === value);
    if (account) setPropFirm(account.prop_firm);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!accountId) {
      setError("Please pick an account.");
      return;
    }
    if (!propFirm) {
      setError("Pick an account first — that sets the prop firm.");
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt)) {
      setError("Amount must be a number.");
      return;
    }

    const input: PayoutInput = {
      date,
      prop_firm: propFirm.trim(),
      account_id: accountId,
      amount: amt,
      notes: notes.trim() || undefined,
    };

    setBusy(true);
    try {
      await onSubmit(input);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit}>
      <h2>{title}</h2>

      <label>
        Date
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </label>

      <label>
        Account
        {loadError ? (
          <span className="error">Could not load accounts: {loadError}</span>
        ) : accounts === null ? (
          <select disabled>
            <option>Loading accounts…</option>
          </select>
        ) : accountOptions.length === 0 ? (
          <span className="muted">
            No active accounts. <Link to="/accounts">Add one</Link>.
          </span>
        ) : (
          <select
            value={accountId}
            onChange={(e) => onAccountChange(e.target.value)}
            onFocus={() => void refresh()}
            required
          >
            <option value="">— pick an account —</option>
            {accountOptions.map((a) => (
              <option key={a.account_id} value={a.account_id}>
                {a.name} ({a.account_number}) — {a.prop_firm}
                {a.active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        )}
      </label>

      <label>
        Prop firm
        <input value={propFirm} readOnly disabled />
        <small>Snapshotted from the chosen account.</small>
      </label>

      <label>
        Amount (USD)
        <input
          type="number"
          step="any"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />
      </label>

      <label>
        Notes (optional)
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Wire reference, percentage split, anything worth remembering."
        />
      </label>

      {error && <div className="error">{error}</div>}

      <div className="form-actions">
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

/** Reduce any ISO datetime to a YYYY-MM-DD value for <input type="date">. */
function toInputDate(value: string): string {
  if (!value) return new Date().toISOString().slice(0, 10);
  // Already YYYY-MM-DD → pass through.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return new Date().toISOString().slice(0, 10);
  return d.toISOString().slice(0, 10);
}
